<template>
  <div class="item">
    <span :class="iconDragClasses" draggable="false">
      <MoreOutlined class="row__icon" />
      <MoreOutlined class="row__icon" />
    </span>
    <span class="row__item row__name">
      <a-tooltip placement="topLeft">
        <template #title>
          <span> {{ item.name }} </span>
        </template>
        {{ item.name }}
      </a-tooltip>
    </span>
    <span class="row__item row__field">
      <a-input
        :value="item.value"
        class="input"
        size="small"
        placeholder="Ingrese nombre aquí"
        :disabled="!item.editable"
        @input="handleInput"
      />
      <a-tooltip>
        <template #title>
          <span v-if="isSelectField">
            Campo tipo lista desplegable<br />
            ({{ optionsCount }} opciones)
          </span>
          <span v-else>Campo tipo texto</span>
        </template>
        <a-tag 
          :color="isSelectField ? 'magenta' : 'gold'"
          class="field-type-tag"
        >
          <DownOutlined v-if="isSelectField" class="field-type-tag__icon"/>
          <TextIcon v-else />
        </a-tag>

      
      </a-tooltip>
    </span>
    
    <a-tooltip v-if="isContact && hasHubspotIntegration">
      <template #title v-if="fixLeadFields.includes(item.name)">
        <span v-if="item.name == ColumnsFixLeadFields.CodeClient">
          El valor de esta columna es autogenerada por BeexCC.
        </span>
        <span v-if="item.name == ColumnsFixLeadFields.FirstName">
          Esta columna se homologó por defecto con su equivalente en BeexCC, al ser un campo obligatorio en común.
        </span>
        <span v-if="item.name == ColumnsFixLeadFields.TypeDocument">
          Esta columna se homologó por defecto con su equivalente en BeexCC. El resto de columnas de tipo listado no se considerarán.
        </span> 
      </template>
      <a-select
        class="row__item select"
        size="small"
        :options="filteredHubspotOptions"
        :value="selectedHubspotProperty"
        show-search
        allow-clear
        :filter-option="filterOption"
        :disabled="isLeadFixed"
        placeholder="Seleccionar"
        @change="handleChangeHubspot"
      >
        <template #option="{ label, value, disabled }">
          <a-tooltip placement="right">
            <template #title v-if="disabled">
              Esta columna ya fue homologada
            </template>
            <div style="min-width: 100%">
              {{ label }}
            </div>
          </a-tooltip>
        </template>
      </a-select>
    </a-tooltip>
    
    <!-- DATO REQUERIDO -->
    <a-row v-if="isContact" class="row__item" type="flex" justify="center">
      <a-col>
        <a-checkbox
          :checked="isRequired"
          :disabled="isLeadFixed && isRequired"
          @change="handleChangeIsRequired(item)"
        />
      </a-col>
    </a-row>

    <!-- INCLUIR EN REPORTE -->
    <a-row class="row__item" type="flex" justify="center">
      <a-col>
        <a-tooltip placement="right">
          <template #title v-if="item.isDisabled">
            Alcanzaste el límite de campos seleccionados.
          </template>
          <span>
            <a-checkbox
              :disabled="isFixed || item.isDisabled"
              :checked="showInReportCheck"
              @change="handleChangeShowInReport(item)"
            />
          </span>
        </a-tooltip>
      </a-col>
    </a-row>

    <!-- INCLUIR EN CALLBACK -->
    <a-row v-if="isContact" class="row__item" type="flex" justify="center">
      <a-col>
        <a-tooltip placement="right">
          <template #title v-if="isFilter">
            Esta columna no permite seleccionar campos de tipo filtro.
          </template>
          <span>
            <a-checkbox
              :disabled="isFixed || isFilter"
              :checked="showInCallbackCheck"
              @change="handleChangeShowInCallback(item)"
            />
          </span>
        </a-tooltip>
      </a-col>
    </a-row>

    <!-- MOSTRAR EN WORKSPACE PARA PRODUCTOS -->
    <a-row v-if="!disabled && isProducts" class="row__item" type="flex" justify="center">
      <a-col>
        <a-checkbox :checked="checked" @change="handleChange(item)" />
      </a-col>
    </a-row>

    <!-- INCLUIR EN LEAD -->
    <a-row v-if="isContact" class="row__item" type="flex" justify="center">
      <a-col>
        <a-checkbox
          :disabled="isLeadFixed"
          :checked="isAcceptedInLead"
          @change="handleChangeIsAcceptedInLead(item)"
        />
      </a-col>
    </a-row>

    <!-- VER EN WORKSPACE CHECKBOX -->
    <a-row v-if="isContact" class="row__item" type="flex" justify="center">
      <a-col>
        <a-checkbox
          :checked="showInWorkspaceIsChecked"
          :disabled="isCodeClient"
          @change="handleChange(item)"
        />
      </a-col>
    </a-row>

    <!-- ACCION ELIMINAR EN CONTACTOS -->
    <a-row v-if="isContact" class="row__item" type="flex" justify="center">
      <a-col>
        <a-tooltip>
          <template v-slot:title v-if="!isCodeClient && !isFirstName"> Eliminar </template>
          <a-button type="link" :disabled="isCodeClient || isFirstName" @click="handleRemove(item)">
            <DeleteOutlined />
          </a-button>
        </a-tooltip>
      </a-col>
    </a-row>

    <a-row v-if="!disabled && isProducts" class="row__item" type="flex" justify="center">
      <a-col>
        <a-tooltip>
          <template v-slot:title> Eliminar </template>
          <a class="edit" @click="handleRemove(item)">
            <DeleteOutlined />
          </a>
        </a-tooltip>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { DeleteOutlined, MoreOutlined, DownOutlined } from "@ant-design/icons-vue";
import { defineComponent } from "vue";
import EventBus from "@/app/shared/utils/eventBus";
import { ColumnsFixLeadFields } from "@/app/imports/utils/enums";
import TextIcon from "@/app/imports/components/icons/TextIcon.vue";

export default defineComponent({
  components: {
    MoreOutlined,
    DeleteOutlined,
    DownOutlined,
    TextIcon,
  },
  props: {
    item: {
      required: true,
      type: Object,
    },
    index: {
      required: true,
      type: Number,
    },
    templateName: {
      required: true,
      type: String,
    },
    propertiesContactHubspot: {
      type: Array,
      required: false,
      default: () => [],
    },
    hasHubspotIntegration: {
      type: Boolean,
      required: false,
      default: false,
    },
    usedHubspotProperties: {
      type: Array,
      required: false,
      default: () => [],
    }
  },
  setup() {
    return { ColumnsFixLeadFields };
  },
  data() {
    return {
      checked: false,
      checkedIsRequired: false,
      showInReportCheck: false,
      showInCallbackCheck: false,
      isAcceptedInLead: false,
      maxFieldsInReport: 14,
      fixFields: ["code_client", "first_name", "maternal_surname", "paternal_surname"],
      fixLeadFields: ["code_client", "first_name","type_document"],
    };
  },

  computed: {
    fullItem() {
      return { ...this.item, index: this.index };
    },
    disabled() {
      return this.item?.disabled || false;
    },
    isEditable() {
      return !this.item.editable;
    },
    itemProp() {
      return this.item;
    },
    templates() {
      return this.$store.getters["getTemplatesFromPortfolio"];
    },
    actives() {
      return this.$store.getters["getActiveTemplateFields"];
    },
    isCodeClient() {
      return this.item.name === "code_client";
    },
    isContact() {
      return this.templateName === "contactos";
    },
    isProducts() {
      return this.templateName === "productos";
    },
    isFilter() {
      return this.item.name.startsWith("filter");
    },
    isCodeAccount() {
      return this.item.name === "code_account";
    },
    isFirstName() {
      return this.item.name === "first_name";
    },
    isSelectField() {
      return this.item.field_type === "select";
    },
    optionsCount() {
      const pendingOptions = this.$store.getters["getPendingTemplateFieldOptions"]?.[
        this.item.name
      ];

      return pendingOptions ? pendingOptions.options.length : (this.item.options_count ?? 0);
    },
    iconDragClasses() {
      return { row__drag: true, "row__drag--disabled": this.isFixed };
    },
    isFixed() {
      return this.fixFields.includes(this.item.name);
    },
    isLeadFixed() {
      return this.fixLeadFields.includes(this.item.name);
    },
    showInWorkspaceIsChecked: {
      get() {
        return this.isCodeClient ? this.item.show_on_workspace : this.checked;
      },
      set(value) {
        this.checked = value;
      },
    },
    isRequired: {
      get() {
        return this.isCodeClient ? this.item.is_required : this.checkedIsRequired;
      },
      set(value) {
        this.checkedIsRequired = value;
      },
    },
    // Mapeo los campos de las cabeceras
    activeTemplateFields() {
      return this.$store.getters["getActiveTemplateFields"];
    },
    // Cuento la cantidad de cabeceras con el campo show_on_report en true
    countShowInReport() {
      return this.activeTemplateFields.reduce((count, field) => {
        return count + (field.show_on_report ? 1 : 0);
      }, 0);
    },
    hubspotPropertiesOptions() {
      console.log(this.propertiesContactHubspot, 'this.propertiesContactHubspot');
      return this.propertiesContactHubspot.map((p) => {
        const label = p.label;
        const value = p.name;
        return { label, value };
      });
    },
    selectedHubspotProperty() {
      return this.isFirstName ? "firstname" : 
        this.isCodeClient ? "Autogenerado por BeexCC" : 
        this.item.hubspot_property || null;
    },
    filteredHubspotOptions() {
      return this.hubspotPropertiesOptions.map(option => {
        const isUsedInAnotherRow =
          this.usedHubspotProperties.includes(option.value) &&
          option.value !== this.item.hubspot_property

        return {
          ...option,
          disabled: isUsedInAnotherRow,
          tooltip: isUsedInAnotherRow
        }
      })
    }
  },
  mounted() {
    this.__validateMax();
    this.checked = this.item.show_on_workspace;
    this.checkedIsRequired = this.item.is_required;
    this.showInReportCheck = this.item.show_on_report;
    this.showInCallbackCheck = this.item.show_on_callback;
    this.isAcceptedInLead = this.item.is_required_lead;
  },
  methods: {
    handleInput(event) {
      this.itemProp.value = event.target.value;
      this.updateItemInStore(this.item);
    },
    handleChangeHubspot(value) {
      this.updateItemInStore({
        ...this.item,
        hubspot_property: value || null,
      });
    },
    filterOption(input, option) {
      const text = (option.label || "").toString().toLowerCase();
      const val = (option.value || "").toString().toLowerCase();
      const q = (input || "").toLowerCase();
      return text.includes(q) || val.includes(q);
    },
    handleChangeIsRequired(item) {
      this.checkedIsRequired = !this.checkedIsRequired;
      this.updateItemInStore({
        ...this.item,
        is_required: this.checkedIsRequired,
      });
    },
    handleChange(item) {
      this.checked = !this.checked;
      this.updateItemInStore({
        ...this.item,
        show_on_workspace: this.checked,
      });
      EventBus.emit("NAME_UNCHECK_ITEM_TEMPLATE", item);
    },
    __validateMax() {
      this.activeTemplateFields.forEach((field) => {
        if (!field.show_on_report) {
          if (localStorage.getItem("workspace") != "demoprestamype")
            field.isDisabled = this.countShowInReport >= this.maxFieldsInReport;
          else
            field.isDisabled = false; // no tiene limite
        }
      });
    },
    handleChangeShowInReport(item) {
      // Validar si se alcanzó el máximo de campos a mostrar en el reporte
      if (this.countShowInReport >= this.maxFieldsInReport && !this.showInReportCheck && localStorage.getItem("workspace") != "demoprestamype") {
        this.__validateMax();
        return;
      }

      this.showInReportCheck = !this.showInReportCheck;
      this.updateItemInStore({
        ...item,
        show_on_report: this.showInReportCheck,
      });

      this.__validateMax();
    },
    handleChangeShowInCallback(item) {
      this.showInCallbackCheck = !this.showInCallbackCheck;

      this.updateItemInStore({
        ...item,
        show_on_callback: this.showInCallbackCheck,
      });
    },
    handleChangeIsAcceptedInLead(item) {
      this.isAcceptedInLead = !this.isAcceptedInLead;

      this.updateItemInStore({
        ...item,
        is_required_lead: this.isAcceptedInLead,
      });
    },
    handleRemove(item) {
      EventBus.emit("NAME_DELETE_ITEM_TEMPLATE", item);
      this.$store.dispatch("removeTemplateFieldItem", this.item);
      this.__validateMax();
    },
    updateItemInStore(item) {
      console.log(item, 'item updateItemInStore');
      this.$store.dispatch("setTemplateFieldItem", item);
    },
    // Handle drag and drop
    handleDragStart(event) {
      const parent = event.target.parentElement;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setDragImage(parent, 25, 20);
      event.dataTransfer.setData("item", JSON.stringify(this.fullItem));
    },
    handleDrop(event) {
      const item = JSON.parse(event.dataTransfer.getData("item"));
      const payload = { from: item, to: this.fullItem };
      this.$store.dispatch("moveTemplateFieldItem", payload);
    },
  },
  watch: {
    "item.show_on_report": function () {
      this.showInReportCheck = this.item.show_on_report;
    },
    "item.show_on_callback": function () {
      this.showInCallbackCheck = this.item.show_on_callback;
    },
    item() {
      this.checked = this.item.show_on_workspace;
      this.checkedIsRequired = this.item.is_required;
      this.showInReportCheck = this.item.show_on_report;
      this.showInCallbackCheck = this.item.show_on_callback;
      this.isAcceptedInLead = this.item.is_required_lead;
    },
  },
});
</script>

<style lang="sass" scoped>
.item
  padding: 6px 0

.row
  &__drag
    display: flex
    align-items: center
    cursor: pointer

    &--disabled
      opacity: 0.2
      pointer-events: none

  &__icon
    font-size: 20px
    color: $gray-1
    &:last-child
      margin-left: -26px

  &__item
    margin: 0 8px

  &__name
    overflow: hidden
    white-space: nowrap
    text-overflow: ellipsis

  &__field
    display: inline-flex
    gap: 8px
    height: 24px

.input
  width: 92%

.select
  width: 92%
  height: 24px

.field-type-tag
  display: inline-flex
  align-items: center
  justify-content: center
  width: 22px
  height: 22px
  padding: 2px
  &__icon
    font-size: 16px
</style>
